import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Key, Building2, Globe } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface APIConfig {
  id: string;
  nombre: string;
  api_key: string;
  organization_id: string | null;
  entorno: string;
  comentarios: string | null;
  estado: string;
  created_at: string;
}

export default function APIConfigurations() {
  const [configs, setConfigs] = useState<APIConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<APIConfig | null>(null);
  const [newConfig, setNewConfig] = useState({
    nombre: '',
    api_key: '',
    organization_id: '',
    entorno: 'desarrollo',
    comentarios: '',
    estado: 'activo'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from('api_configuraciones')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setConfigs(data || []);
    } catch (error) {
      console.error('Error fetching API configurations:', error);
      setError('Error al cargar las configuraciones de API');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingConfig) {
        const { error } = await supabase
          .from('api_configuraciones')
          .update({
            nombre: newConfig.nombre,
            api_key: newConfig.api_key,
            organization_id: newConfig.organization_id || null,
            entorno: newConfig.entorno,
            comentarios: newConfig.comentarios || null,
            estado: newConfig.estado
          })
          .eq('id', editingConfig.id);

        if (error) throw error;
        setSuccess('Configuración actualizada correctamente');
      } else {
        const { error } = await supabase
          .from('api_configuraciones')
          .insert([{
            nombre: newConfig.nombre,
            api_key: newConfig.api_key,
            organization_id: newConfig.organization_id || null,
            entorno: newConfig.entorno,
            comentarios: newConfig.comentarios || null,
            estado: newConfig.estado
          }]);

        if (error) throw error;
        setSuccess('Configuración creada correctamente');
      }

      setShowModal(false);
      setEditingConfig(null);
      setNewConfig({
        nombre: '',
        api_key: '',
        organization_id: '',
        entorno: 'desarrollo',
        comentarios: '',
        estado: 'activo'
      });
      fetchConfigs();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (config: APIConfig) => {
    setEditingConfig(config);
    setNewConfig({
      nombre: config.nombre,
      api_key: config.api_key,
      organization_id: config.organization_id || '',
      entorno: config.entorno,
      comentarios: config.comentarios || '',
      estado: config.estado
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta configuración? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('api_configuraciones')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Configuración eliminada correctamente');
      fetchConfigs();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleToggleStatus = async (config: APIConfig) => {
    try {
      const newStatus = config.estado === 'activo' ? 'inactivo' : 'activo';
      const { error } = await supabase
        .from('api_configuraciones')
        .update({ estado: newStatus })
        .eq('id', config.id);

      if (error) throw error;
      setSuccess(`Configuración ${newStatus === 'activo' ? 'activada' : 'desactivada'} correctamente`);
      fetchConfigs();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const filteredConfigs = configs.filter(config =>
    config.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.entorno.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (config.comentarios?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Configuraciones de API</h1>
        <button 
          onClick={() => {
            setEditingConfig(null);
            setNewConfig({
              nombre: '',
              api_key: '',
              organization_id: '',
              entorno: 'desarrollo',
              comentarios: '',
              estado: 'activo'
            });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Configuración
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar configuraciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold text-gray-600">Nombre</th>
                  <th className="pb-3 font-semibold text-gray-600">Entorno</th>
                  <th className="pb-3 font-semibold text-gray-600">API Key</th>
                  <th className="pb-3 font-semibold text-gray-600">Organization ID</th>
                  <th className="pb-3 font-semibold text-gray-600">Estado</th>
                  <th className="pb-3 font-semibold text-gray-600">Comentarios</th>
                  <th className="pb-3 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredConfigs.map((config) => (
                  <tr key={config.id} className="border-b">
                    <td className="py-3">
                      <div className="flex items-center">
                        <Globe className="w-5 h-5 text-blue-500 mr-2" />
                        {config.nombre}
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        config.entorno === 'produccion'
                          ? 'bg-red-100 text-red-800'
                          : config.entorno === 'staging'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {config.entorno}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className="font-mono text-sm">
                        {config.api_key.substring(0, 8)}...
                      </span>
                    </td>
                    <td className="py-3">
                      <span className="font-mono text-sm">
                        {config.organization_id
                          ? `${config.organization_id.substring(0, 8)}...`
                          : '-'}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        config.estado === 'activo'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {config.estado}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className="text-sm text-gray-600">
                        {config.comentarios || '-'}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(config)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Editar configuración"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(config.id)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                          title="Eliminar configuración"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredConfigs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron configuraciones</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir/editar configuración */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingConfig ? 'Editar Configuración' : 'Nueva Configuración'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={newConfig.nombre}
                  onChange={(e) => setNewConfig({...newConfig, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Ej: OpenAI Producción"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key
                </label>
                <input
                  type="password"
                  required
                  value={newConfig.api_key}
                  onChange={(e) => setNewConfig({...newConfig, api_key: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="sk-..."
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization ID
                </label>
                <input
                  type="text"
                  value={newConfig.organization_id}
                  onChange={(e) => setNewConfig({...newConfig, organization_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="org-..."
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entorno
                </label>
                <select
                  value={newConfig.entorno}
                  onChange={(e) => setNewConfig({...newConfig, entorno: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="desarrollo">Desarrollo</option>
                  <option value="staging">Staging</option>
                  <option value="produccion">Producción</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comentarios
                </label>
                <textarea
                  rows={3}
                  value={newConfig.comentarios}
                  onChange={(e) => setNewConfig({...newConfig, comentarios: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Notas adicionales..."
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  value={newConfig.estado}
                  onChange={(e) => setNewConfig({...newConfig, estado: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingConfig(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingConfig ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}